import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { CalendarAnalyticsResult } from '@/lib/api';

interface UseAnalyticsResult {
  data: CalendarAnalyticsResult | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAnalytics(start: string, end: string): UseAnalyticsResult {
  const [data, setData] = useState<CalendarAnalyticsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  const refetch = useCallback(() => setTrigger((t) => t + 1), []);

  useEffect(() => {
    if (!start || !end) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getCalendarAnalytics(start, end)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load analytics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [start, end, trigger]);

  return { data, loading, error, refetch };
}
